import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Location} from 'react-router-dom'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {AuthoritySettings, SubUser} from '../residentUser/repository/NatGetResidentUserInvolvingSub'
import {Nullable} from '../../../app/TypeHelpers'
import {Box} from '@mui/material';
import {StyledPageTitle, StyledSecondaryButton} from '../../../components/reservations/components/CommonStyle'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../styledComponents/ResponsiveConvenience'
import {
    CodeMasterInterface,
    MESSAGE_TYPE,
    SUB_USER_AUTHORITY_SETTING_ACCOUNT,
    SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT,
    VALID_OR_NOT
} from '../../../CodeMaster'
import {DisplayItemDividerForPageTop, DisplayItemInputTitle} from '../../../styledComponents/DisplayItem'
import {PlainLinkStyledBackButton, PrimaryStyledButton} from '../../../styledComponents/Button'
import {Messages} from '../../../Messages'
import {
    AUTHORITY_ISSUE,
    AUTHORITY_VIEW,
    FUNCTION_DIV_ID_FOR_RESIDENT_ACCOUNT_INFORMATION,
    FUNCTION_DIV_ID_FOR_RESIDENT_BILLING_AMOUNT,
    FUNCTION_DIV_ID_FOR_RESIDENT_RECEIPT
} from '../condominiumAuthoritySetting/NatAdminCondominiumAuthorityConstant'
import NatAuthorityRepo, {
    UpdateSubUserAuthoritySetting,
    UpdateSubUserAuthoritySettingRequest
} from '../authority/repository/NatAuthorityRepo';
import PPRadioGroupForResident from '../../../components/shared/forms/resident/PPRadioGroupForResident';
import NatResidentMessageDialogComponent from '../common/resident/NatResidentMessageDialogComponent';
import {HKConstants} from '../../../HKConstants';
import AuthorityChecker from '../../../components/AuthorityChecker';
import PartialAuthorityChecker from '../../../components/PartialAuthorityChecker';

export interface ChangeUserUseRestrictionSettingLocationState {
    subUserInfo: SubUser,
}

interface Props {
    condominiumCd: string,
    ridgeCd: string,
    roomNumber: string,
    seq: number,
    authorityRepo: NatAuthorityRepo,
    location: Location,
}

interface States {
    subUserInfo: SubUser
    subUserAuthoritySettingBillingReceipt: Nullable<SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT>
    subUserAuthoritySettingAccount: Nullable<SUB_USER_AUTHORITY_SETTING_ACCOUNT>
    openChangeDialog: boolean
    isLoadCompleted: boolean
}

class NatResidentUserUseRestrictionSettingPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            subUserInfo: {
                seq: 0,
                userId: '',
                familyName: '',
                firstName: '',
                mailAddressPc: null,
                mailAddressMb: null,
                facilityReserveAvailability: '',
                authoritySettings: null,
            },
            subUserAuthoritySettingBillingReceipt: null,
            subUserAuthoritySettingAccount: null,
            openChangeDialog: false,
            isLoadCompleted: false
        }

        this.handleChangeClick = this.handleChangeClick.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleChangeOkClick = this.handleChangeOkClick.bind(this)
        this.backButtonClick = this.backButtonClick.bind(this)
        this.handleChangeBillingReceipt = this.handleChangeBillingReceipt.bind(this)
        this.handleChangeAccount = this.handleChangeAccount.bind(this)
    }

    async componentDidMount(): Promise<void> {
        try {
            if (this.props.location.state.subUserInfo) {
                this.setState({
                    subUserInfo: this.props.location.state.subUserInfo,
                })
                this.setSubUserAuthority(this.props.location.state.subUserInfo.authoritySettings ?? [])
            }
        } finally {
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    setSubUserAuthority(authoritySettings: AuthoritySettings[]): void {
        const receipt = authoritySettings.find(authoritySetting =>
            authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_FOR_RESIDENT_RECEIPT
            && authoritySetting.authorityId === AUTHORITY_ISSUE)
        const billingAmount = authoritySettings.find(authoritySetting =>
            authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_FOR_RESIDENT_BILLING_AMOUNT
            && authoritySetting.authorityId === AUTHORITY_VIEW)
        const accountInformation = authoritySettings.find(authoritySetting =>
            authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_FOR_RESIDENT_ACCOUNT_INFORMATION
            && authoritySetting.authorityId === AUTHORITY_VIEW)

        if (billingAmount || receipt) {
            let valid: CodeMasterInterface
            if (receipt?.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_POSSIBLE_ISSUE_POSSIBLE
            } else if (billingAmount?.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_POSSIBLE_ISSUE_IMPOSSIBLE
            } else {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_IMPOSSIBLE
            }
            this.setState({
                subUserAuthoritySettingBillingReceipt: valid
            })
        }

        if (accountInformation) {
            let valid: CodeMasterInterface
            if (accountInformation.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_ACCOUNT.BROWSE_POSSIBLE
            } else {
                valid = SUB_USER_AUTHORITY_SETTING_ACCOUNT.BROWSE_IMPOSSIBLE
            }
            this.setState({
                subUserAuthoritySettingAccount: valid
            })
        }
    }

    backButtonClick(): void {
        const {navigate, condominiumCd, ridgeCd, roomNumber, seq} = this.props
        navigate(`/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users/${seq}`)
    }

    handleChangeClick(): void {
        this.setState({openChangeDialog: true})
    }

    handleCancelClick(): void {
        this.setState({openChangeDialog: false})
    }

    createAuthoritySettings(): AuthoritySettings[] {
        const {subUserAuthoritySettingBillingReceipt, subUserAuthoritySettingAccount} = this.state
        const authoritySettings: AuthoritySettings[] = []
        if (subUserAuthoritySettingBillingReceipt) {
            if (subUserAuthoritySettingBillingReceipt
                === SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_IMPOSSIBLE) {
                authoritySettings.push({
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_BILLING_AMOUNT,
                        authorityId: AUTHORITY_VIEW,
                        validFlag: VALID_OR_NOT.NOT.value,
                    },
                    {
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_RECEIPT,
                        authorityId: AUTHORITY_ISSUE,
                        validFlag: VALID_OR_NOT.NOT.value,
                    },
                )
            } else if (subUserAuthoritySettingBillingReceipt
                === SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_POSSIBLE_ISSUE_IMPOSSIBLE) {
                authoritySettings.push({
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_BILLING_AMOUNT,
                        authorityId: AUTHORITY_VIEW,
                        validFlag: VALID_OR_NOT.VALID.value,
                    },
                    {
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_RECEIPT,
                        authorityId: AUTHORITY_ISSUE,
                        validFlag: VALID_OR_NOT.NOT.value,
                    },)
            } else {
                authoritySettings.push({
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_BILLING_AMOUNT,
                        authorityId: AUTHORITY_VIEW,
                        validFlag: VALID_OR_NOT.VALID.value,
                    },
                    {
                        functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_RECEIPT,
                        authorityId: AUTHORITY_ISSUE,
                        validFlag: VALID_OR_NOT.VALID.value,
                    },)
            }
        }
        if (subUserAuthoritySettingAccount) {
            if (subUserAuthoritySettingAccount === SUB_USER_AUTHORITY_SETTING_ACCOUNT.BROWSE_IMPOSSIBLE) {
                authoritySettings.push({
                    functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_ACCOUNT_INFORMATION,
                    authorityId: AUTHORITY_VIEW,
                    validFlag: VALID_OR_NOT.NOT.value,
                })
            } else {
                authoritySettings.push({
                    functionAuthorityDivisionId: FUNCTION_DIV_ID_FOR_RESIDENT_ACCOUNT_INFORMATION,
                    authorityId: AUTHORITY_VIEW,
                    validFlag: VALID_OR_NOT.VALID.value,
                })
            }
        }

        return authoritySettings
    }

    async handleChangeOkClick(): Promise<void> {
        const {eventEmitter, authorityRepo, condominiumCd, ridgeCd, roomNumber, seq} = this.props
        const requestParam: UpdateSubUserAuthoritySettingRequest = {authoritySettings: this.createAuthoritySettings()}

        this.setState({openChangeDialog: false})
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<UpdateSubUserAuthoritySetting> = await this.props.asyncCallFunction(
                this.props.authorityRepo.updateSubUserAuthoritySetting.bind(
                    authorityRepo,
                    {
                        condominiumCd: condominiumCd,
                        ridgeCd: ridgeCd,
                        roomNumber: roomNumber,
                        seq: seq,
                        requestParam: requestParam,
                    }
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )

            if (response && response.requestError === null) {
                this.props.navigate(`/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users/${seq}`)
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleChangeBillingReceipt(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            subUserAuthoritySettingBillingReceipt: SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.codeToEnumConstants(
                event.target.value
            )
        })
    }

    handleChangeAccount(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            subUserAuthoritySettingAccount: SUB_USER_AUTHORITY_SETTING_ACCOUNT.codeToEnumConstants(
                event.target.value
            )
        })
    }

    renderChangeDialog(): ReactElement {
        const message: string = Messages.IOPCC0028.toString(`利用制限`)

        return (
            <NatResidentMessageDialogComponent
                open={this.state.openChangeDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.WARNING,
                    message: message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelClick,
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleChangeOkClick,
                        },
                    ],
                }}
            />
        )
    }

    renderUserInfo(): ReactElement {
        const {familyName, firstName, mailAddressPc, mailAddressMb} = this.state.subUserInfo

        return (
            <WideWhiteArea>
                <Box>
                    <Box fontWeight={FontWeights.Bold} component={'span'}>{familyName} {firstName}</Box>
                </Box>
                <Box pt={'10px'}>
                    <UserTypeLabel>サブユーザ</UserTypeLabel>
                </Box>
                {mailAddressPc && <Box pt={'10px'}>{mailAddressPc}</Box>}
                {mailAddressMb && <Box pt={'10px'}>{mailAddressMb}</Box>}
            </WideWhiteArea>
        )
    }

    renderSubUserAuthority(): ReactElement {
        const {subUserAuthoritySettingBillingReceipt, subUserAuthoritySettingAccount} = this.state

        return (
            <>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.RESTRICTION_SETTING_BILLING_AMOUNT}>
                        <BlockSpacer height={'15px'}/>
                        <ItemHeading>請求金額の閲覧・領収書発行</ItemHeading>
                        <WideWhiteArea>
                            <GridContainer>
                                <DisplayDescription>管理費等お知らせからご請求金額を閲覧できる、お支払いが完了した内容の領収書を発行できる</DisplayDescription>
                                <GridItem over={12}>
                                    <PPRadioGroupForResident
                                        data={SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.values()}
                                        selectValue={subUserAuthoritySettingBillingReceipt?.value}
                                        onChange={this.handleChangeBillingReceipt}
                                        eventKeys={['billingReceipt']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </GridItem>
                            </GridContainer>
                            <Box height={'10px'}/>
                        </WideWhiteArea>
                    </AuthorityChecker>
                    <AuthorityChecker permittedAuthorities=
                                          {HKConstants.CMN.AUTH.RESTRICTION_SETTING_ACCOUNT_INFORMATION}>
                        <BlockSpacer height={'15px'}/>
                        <ItemHeading>口座情報の閲覧</ItemHeading>
                        <WideWhiteArea>
                            <GridContainer>
                                <DisplayDescription>口座振替の情報から口座情報を閲覧できる</DisplayDescription>
                                <GridItem over={12}>
                                    <PPRadioGroupForResident
                                        data={SUB_USER_AUTHORITY_SETTING_ACCOUNT.values()}
                                        selectValue={subUserAuthoritySettingAccount?.value}
                                        onChange={this.handleChangeAccount}
                                        eventKeys={['account']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </GridItem>
                            </GridContainer>
                            <Box height={'10px'}/>
                        </WideWhiteArea>
                    </AuthorityChecker>
            </>
        )
    }

    render(): ReactElement {
        const {isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return <></>
        }

        return (<>
            {this.renderChangeDialog()}
            <PlainLinkStyledBackButton onClick={this.backButtonClick}>
                戻る
            </PlainLinkStyledBackButton>
            <BlockSpacer height={'14px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>利用制限の設定</StyledPageTitle>
            <Box height={'20px'}/>
            {this.renderUserInfo()}
            {this.renderSubUserAuthority()}
            <BlockSpacer height={'20px'}/>
            <PartialAuthorityChecker permittedAuthorities=
                                    {[HKConstants.CMN.AUTH.RESTRICTION_SETTING_BILLING_AMOUNT,
                                        HKConstants.CMN.AUTH.RESTRICTION_SETTING_ACCOUNT_INFORMATION]
                                    }>
                <PrimaryStyledButton
                    onClick={this.handleChangeClick}>
                    変更する
                </PrimaryStyledButton>
                <Box height={'30px'}/>
            </PartialAuthorityChecker>
            <StyledSecondaryButton onClick={this.backButtonClick}>
                戻る
            </StyledSecondaryButton>
        </>)
    }
}

export default createResidentWithErrorHandlingPage(NatResidentUserUseRestrictionSettingPage)

const WideWhiteArea = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  padding: 10px 15px;
  box-sizing: border-box;
  overflow: hidden;
`

const UserTypeLabel = styled.label`
  font-size: ${FontSize.Middle};
  color: ${Colors.AccentDark};
  background: rgba(242,242,242,1);
  line-height: 21px;
  padding: 2px 10px;
  border-radius: 4px;
`

const ItemHeading = styled.div`
  background-color: ${Colors.AccentLight};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  font-size: ${FontSize.Body};
  padding: 6px 15px 6px;
  box-sizing: border-box;
  overflow: hidden;
`

const DisplayDescription = styled(DisplayItemInputTitle)`
  color: ${Colors.AccentDark};
  margin-top: 5px;
  ${currentMedia.lessThan('sp')`
    margin-bottom: 17px;
  `}
  ${currentMedia.greaterThan('pc')`
    margin-bottom: 10px;
  `}
`
